
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

import {
  CHECKBOX_GROUP_LIST,
  CHECKBOX_LIST,
  SLIDER,
  INPUT,
  AUTOCOMPLETE_INPUT,
  CHECKBOX,
  DROPDOWN,
  DROPDOWN_BOX_SELECT,
  DROP_DOWN_SLIDER,
  UP_DOWN_NUMBER_BOX,
  TOGGLE_BOXES,
  PERIOD_DROPDOWN,
} from 'Defaults/filters'
import { filterCheckboxListGroupByHideOn } from 'Utils/searchFilterUtils'

import { PanelCheckbox } from 'Components/SearchFilterFactory/PanelForms'
import AppDropdown from '../PanelForms/AppDropdown'
import {
  SliderInput,
  DatePicker,
  PanelInput,
  AutocompleteInput,
  CheckboxList,
  DropdownMultiSelect,
  DropdownSlider,
  MultiSelectBoxes,
  UpDownNumberBoxController,
  ToggleBoxes,
  MultiCollapsible,
} from '../PanelForms'

import eventHandlerFactory from './eventHandlerFactory'

import { _SliderContainer, _DatePickerContainer, _DropdownContainer } from './styles'

const stopPropa = (e) => {
  e.stopPropagation()
}

const eventHandlerMemo = (evtHandlerFactory) => {
  let eventHandlers = {}
  const setHandler = ({ componentData, componentName, inputName, googleAnalyticsEvent, checkHideState }) => {
    eventHandlers[componentName + inputName] =
      evtHandlerFactory(componentData, componentName, inputName, googleAnalyticsEvent, checkHideState)
    return eventHandlers[componentName + inputName]
  }
  const getHandler = ({ componentData, componentName, inputName, googleAnalyticsEvent, checkHideState }) => {
    if(eventHandlers[componentName + inputName]) {
      return eventHandlers[componentName + inputName]
    }
    return setHandler({ componentData, componentName, inputName, googleAnalyticsEvent, checkHideState })
  }
  const purgeAllHandlers = () => {
    eventHandlers = {}
  }
  return {
    getHandler,
    purgeAllHandlers,
  }
}
const { getHandler, purgeAllHandlers } = (eventHandlerMemo(eventHandlerFactory))

export const FormFactory = (
  {
    isBasic,
    componentData,
    componentName,
    inputName,
    section,
    hideOn,
    searchQueryObj,
    googleAnalyticsEvent,
    startShowMobile,
    checkHideState = () => {},
    mobileFullPagePopup,
    isMobileApp,
    gaFromComponent,
    gaFromPage,
  },
) => {
  const eventHandler = getHandler(
    {
      componentData,
      componentName,
      inputName,
      googleAnalyticsEvent,
      checkHideState,
    },
  )
  useEffect(() =>
    // free up memory on handler cache
    () => purgeAllHandlers(), [])
  if(isBasic && !startShowMobile) {
    switch(componentName) {
      case TOGGLE_BOXES:
        return (
          <ToggleBoxes
            onChange={eventHandler}
            items={componentData.items}
            selectedVal={searchQueryObj[inputName]}
          />
        )
      case DROPDOWN:
        return (
          <AppDropdown
            // this is edge cases only for mobile and period dropdown
            options={componentData.items}
            label={componentData.label}
            onChange={eventHandler}
            selectedVal={
              searchQueryObj[inputName] ||
              componentData.defaultValue ||
              -1
            }
            mobileFullPagePopup={mobileFullPagePopup}
            isMobileApp={isMobileApp}
            gaFromComponent={gaFromComponent}
            gaFromPage={gaFromPage}
          />
        )
      case DROP_DOWN_SLIDER:
        return (
          <DropdownSlider
            key={section}
            dropdownContentSide={'left'}
            metaData={componentData}
            label={componentData.label}
            inputName={inputName}
            section={section}
            selectedVal={searchQueryObj[inputName] || ''}
            onChange={eventHandler}
            mobileFullPagePopup={mobileFullPagePopup}
            isMobileApp={isMobileApp}
            gaFromComponent={gaFromComponent}
            gaFromPage={gaFromPage}
          />
        )
      case DROPDOWN_BOX_SELECT:
        // options, onClick, label, selectedVal
        return (
          <DropdownMultiSelect
            useGrid
            key={section}
            metaData={componentData}
            label={componentData.label}
            dropdownContentSide={'left'}
            options={componentData.items}
            onClick={eventHandler}
            selectedVal={searchQueryObj[inputName] || []}
          />
        )
    }
  } else {
    switch(componentName) {
      case TOGGLE_BOXES:
        return (
          <ToggleBoxes
            onChange={eventHandler}
            items={componentData.items}
            selectedVal={searchQueryObj[inputName]}
          />
        )
      case UP_DOWN_NUMBER_BOX:
        return (
          <UpDownNumberBoxController
            sectionLabel={componentData.label}
            selectedVal={searchQueryObj[inputName] || ''}
            items={componentData.items}
            onUpdate={eventHandler}
          />
        )
      case 'MultiSelectBoxes':
        return (
          <MultiSelectBoxes
            sectionLabel={componentData.label}
            selectedVal={searchQueryObj[inputName] || ''}
            items={componentData.items}
            column={componentData.column || { large: 2, medium: 2, small: 2 }}
            onUpdate={eventHandler}
          />
        )
      case DROPDOWN:
      case PERIOD_DROPDOWN:
        return (
          <_DropdownContainer
            periodDropdown={componentName === PERIOD_DROPDOWN}
          >
            <AppDropdown
              mobileFullPagePopup={mobileFullPagePopup}
              options={componentData.items}
              label={componentData.label}
              onChange={eventHandler}
              selectedVal={
                searchQueryObj[inputName] ||
                componentData.defaultValue ||
                -1
              }
            />
          </_DropdownContainer>
        )
      case INPUT:
        if(componentData.type === 'date') {
          return (
            <_DatePickerContainer
              key={inputName}
            >
              <DatePicker
                inputName={inputName}
                {...componentData}
                placeholder={componentData.format}
                onChange={eventHandler}
              />
            </_DatePickerContainer>
          )
        }
        return (
          <PanelInput
            key={inputName}
            inputName={inputName}
            {...componentData}
            handleUpdate={eventHandler}
            hideOn={hideOn}
          />
        )
      case AUTOCOMPLETE_INPUT:
        return (
          <AutocompleteInput
            key={inputName}
            inputName={inputName}
            selectedVal={searchQueryObj[inputName] || ''}
            {...componentData}
            handleUpdate={eventHandler}
            hideOn={hideOn}
          />
        )

      case SLIDER:
      case DROP_DOWN_SLIDER:
        // min, max coming from url, look at SliderInput index.js
        const { intervals, prefix, suffix, noComma } = componentData
        return (
          <_SliderContainer
            key={inputName}
            onClick={stopPropa}
          >
            <SliderInput
              inputName={inputName}
              intervals={intervals}
              prefix={prefix}
              suffix={suffix}
              noComma={noComma}
              handleSliderAfterChange={eventHandler}
              section={section}
            />
          </_SliderContainer>
        )
      case CHECKBOX:
        const { items } = componentData
        const { label, uid, value } = items[0]
        return (
          <PanelCheckbox
            key={inputName}
            inputName={inputName}
            label={label}
            uid={uid}
            value={value}
            updateHandler={eventHandler}
          />
        )
      case CHECKBOX_LIST:
      case DROPDOWN_BOX_SELECT:
        const checkboxListUpdateHandler =
          eventHandlerFactory(componentData, CHECKBOX_LIST, inputName, googleAnalyticsEvent)
        const checkboxListSelectedVal = searchQueryObj[inputName] ? searchQueryObj[inputName].split(',') : []
        return (
          <CheckboxList
            items={componentData.items}
            filter={componentData.filter}
            selectedVals={checkboxListSelectedVal}
            updateHandler={checkboxListUpdateHandler}
            layout={'inline'}
          />
        )
      case CHECKBOX_GROUP_LIST:
        const filteredData = filterCheckboxListGroupByHideOn(componentData, searchQueryObj)
        const { pivot, keys, childItems } = filteredData
        return (
          <MultiCollapsible
            selectedVal={searchQueryObj[inputName] || ''}
            pivot={pivot}
            keys={keys}
            childItems={childItems}
            parentBoxClickHandler={eventHandler.parentCheckboxHandler}
            subBoxClickHandler={eventHandler.subCheckboxListHandler}
          />
        )
    }
  }
  return null
}

FormFactory.propTypes = {
  isBasic: PropTypes.bool,
  componentData: PropTypes.shape({
    type: PropTypes.string,
    format: PropTypes.string,
    filter: PropTypes.string,
    intervals: PropTypes.arrayOf(PropTypes.shape({})),
    prefix: PropTypes.string,
    suffix: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      uid: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
    column: PropTypes.shape({}),
    label: PropTypes.string,
    defaultValue: PropTypes.string,
    noComma: PropTypes.bool,
  }),
  componentName: PropTypes.string,
  inputName: PropTypes.string,
  section: PropTypes.string,
  hideOn: PropTypes.shape({}),
  searchQueryObj: PropTypes.shape({}),
  googleAnalyticsEvent: PropTypes.func,
  checkHideState: PropTypes.func,
  startShowMobile: PropTypes.bool,
  mobileFullPagePopup: PropTypes.bool,
  isMobileApp: PropTypes.bool,
  gaFromComponent: PropTypes.string,
  gaFromPage: PropTypes.string,
}

